import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Body,
  Button,
  Card,
  Heading,
  I,
  Stack,
} from '@leanin/ui';
import { FlowShell, type FlowStep } from '@/components/onboarding/FlowShell';
import { trackOnboardingStepCompleted } from '@/lib/analytics/events';
import { flowStepParams } from '@/lib/analytics/onboarding-steps';
import { Picture } from '@/components/media/Picture';
import circlesDirectory from '@/assets/onboarding/circles-directory.png?w=440;880;1320&format=avif;webp;png&as=picture';

// Circle Member onboarding tour — step 2 of 4 (welcome + community merged).
const STEPS: ReadonlyArray<FlowStep> = [
  { label: 'Welcome' },
  { label: 'Find a Circle to join' },
  { label: 'Connect with your community' },
  { label: 'Build leadership skills' },
  { label: "You're in" },
];

export const Route = createFileRoute('/onboarding/cm/find-circle')({
  component: CMOnboardingFindCirclePage,
});

function CMOnboardingFindCirclePage() {
  const navigate = useNavigate();

  return (
    <FlowShell
      role="Circle Member"
      stepIndex={1}
      steps={STEPS}
      minutes={3}
      title="Find a Circle"
      primary={
        <Button
          variant="primary"
          icon={I.arrow}
          onClick={() => {
            trackOnboardingStepCompleted(
              flowStepParams('/onboarding/cm/find-circle'),
            );
            navigate({ to: '/onboarding/cm/connect' });
          }}
        >
          Next
        </Button>
      }
      secondary={
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/onboarding/cm/welcome' })}
        >
          Back
        </Button>
      }
    >
      <Stack gap="lg">
        <Heading level={2}>Find a Circle</Heading>
        <Body size="md">
          Lean In Circles are small groups that meet regularly to share
          experiences, exchange advice, build confidence, and support one
          another through real conversations. We’re excited for you to join
          one.
        </Body>

        <Stack gap="md">
          <Body size="md">
            Browse the Circles directory to filter by topic, location, or
            shared interests. When you request to join, you’ll share a short
            message that goes to the Circle Leader.
          </Body>
          <Card pad="flush" clip>
            <Picture
              image={circlesDirectory}
              alt="Circles directory showing search, filters, and a grid of Circle cards"
              sizes="(max-width: 720px) 100vw, 880px"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </Card>
        </Stack>
      </Stack>
    </FlowShell>
  );
}
